import React, { createContext, useContext, useState, useCallback } from 'react'
import { SunlightData, EnhancedSunlightData } from '@popety-io/types'
import * as sunlightService from '../services/sunlightService'

interface SunlightParams {
  landId?: string
  buildingId?: string
  latitude?: number
  longitude?: number
  useMockData?: boolean
}

interface SunlightState {
  data: SunlightData | EnhancedSunlightData | null
  loading: boolean
  error: string | null
}

interface SunlightContextValue extends SunlightState {
  fetchSunlight: (params: SunlightParams) => Promise<void>
  clearSunlight: () => void
}

const SunlightContext = createContext<SunlightContextValue | undefined>(
  undefined
)

export const SunlightProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [state, setState] = useState<SunlightState>({
    data: null,
    loading: false,
    error: null,
  })

  const fetchSunlight = useCallback(async (params: SunlightParams) => {
    setState((prev) => ({ ...prev, loading: true, error: null }))

    try {
      let data: SunlightData | EnhancedSunlightData

      if (params.landId) {
        // Fetch by land ID
        data = await sunlightService.getSunlightStatisticsForLand(
          params.landId,
          params.latitude && params.longitude
            ? { lat: params.latitude, lon: params.longitude }
            : undefined
        )
      } else if (params.buildingId) {
        // Fetch by building ID
        data = await sunlightService.getSunlightStatisticsForBuilding(
          params.buildingId
        )
      } else if (params.latitude && params.longitude) {
        // Calculate from coordinates using enhanced frontend calculator
        const { calculateEnhancedSunlightData } =
          await import('../services/sunlightCalculatorEnhanced')
        data = calculateEnhancedSunlightData({
          lat: params.latitude,
          lon: params.longitude,
        })
      } else {
        throw new Error(
          'Either landId, buildingId, or coordinates must be provided'
        )
      }

      setState({ data, loading: false, error: null })
    } catch (error) {
      setState((prev) => ({
        ...prev,
        loading: false,
        error:
          error instanceof Error
            ? error.message
            : 'Failed to fetch sunlight data',
      }))
    }
  }, [])

  const clearSunlight = useCallback(() => {
    setState({ data: null, loading: false, error: null })
  }, [])

  return (
    <SunlightContext.Provider
      value={{ ...state, fetchSunlight, clearSunlight }}
    >
      {children}
    </SunlightContext.Provider>
  )
}

export const useSunlight = () => {
  const context = useContext(SunlightContext)
  if (!context) {
    throw new Error('useSunlight must be used within a SunlightProvider')
  }
  return context
}
